/**
 * lib/deviceSession.ts — the client half of the 3-concurrent-device cap.
 *
 * One tiny observable store plus the three calls that drive it. It is
 * deliberately NOT in the Zustand app store: this state must survive being read
 * before React mounts (the auth tick can beat the tree), and it must be
 * writable from `lib/firebase.ts` and from the API transport without either of
 * them importing the UI.
 *
 * ── THE ONE RULE ──────────────────────────────────────────────────────────
 * A device-registry problem must NEVER be able to lock a user out of the app.
 * Every failure that isn't an explicit, well-formed "you are at your limit" or
 * "this device was signed out" resolves to `idle` — the feature simply isn't
 * there. The route 404s while DEVICE_LIMIT_LIVE is unset, and that 404 is the
 * normal case today, not an error.
 */
import { api, type SessionFailure } from '../api/client'
import { deviceDescriptor, getDeviceId } from './deviceId'
import { ApiError } from '../api/types'

export interface DeviceSummary {
  deviceId: string
  label: string
  platform: 'web' | 'ios' | 'android' | 'pwa'
  native: boolean
  firstSeenAt: string
  lastSeenAt: string
  status: 'active' | 'evicted'
  current: boolean
}

/**
 * WHY the limit state carries a cause.
 *
 * `device_evicted` and `device_not_registered` feed the SAME overlay, and the
 * honest copy for each is different. Arriving at the cap on a fresh sign-in
 * ("you're signed in on 3 devices") is a different sentence from being stopped
 * mid-session because your slot went away underneath you ("your session here
 * was replaced") — and telling a user who was working five seconds ago that
 * they have just signed in is how a security screen starts reading as a bug.
 */
export type LimitCause = 'signin' | 'interrupted'

export type DeviceGateState =
  /** Nothing to show. Includes "the feature is off", which is today's prod. */
  | { kind: 'idle' }
  /** The 4th sign-in. The user must choose which session ends — never us. */
  | {
    kind: 'limit'; devices: DeviceSummary[]; limit: number; busy: boolean
    error: string | null; cause: LimitCause
  }
  /** This device was signed out from somewhere else. */
  | { kind: 'evicted'; message: string }
  /** The token stopped being valid (revoked, deleted account, clock skew). */
  | { kind: 'signed_out' }

let state: DeviceGateState = { kind: 'idle' }
const subs = new Set<() => void>()

/**
 * ── THE ANTI-RECURRENCE WINDOW ────────────────────────────────────────────
 *
 * The moment a claim succeeds, requests that were already in flight can still
 * come back 403 `device_evicted` / `device_not_registered` — they were answered
 * against the state of the world BEFORE the eviction landed. Trusting one of
 * those raises a wall immediately after the user just successfully cleared it,
 * which is the exact shape of the incident this whole surface exists to avoid:
 * act, succeed, hit the same wall.
 *
 * So for a few seconds after a successful claim we do not trust a device 403 —
 * we RE-DIAGNOSE by claiming once more. A genuinely dead session still ends up
 * at the right screen (claim 403s, `claimDevice` raises `evicted`); a racing
 * stale response resolves invisibly, which is what it deserves.
 */
const CLAIM_GRACE_MS = 6000
let lastClaimOkAt = 0
let rediagnosing = false

export function getDeviceGateState(): DeviceGateState { return state }
export function subscribeDeviceGate(fn: () => void): () => void {
  subs.add(fn)
  return () => { subs.delete(fn) }
}
function setState(next: DeviceGateState): void {
  state = next
  subs.forEach((f) => { try { f() } catch { /* one bad subscriber must not stop the rest */ } })
}
export function clearDeviceGate(): void { setState({ kind: 'idle' }) }

/**
 * Full reset of this module's singleton — the gate AND the anti-recurrence
 * window below. Called on sign-out, so the next person to sign in on a shared
 * office workstation inherits neither a wall nor somebody else's grace period.
 * (`clearDeviceGate` deliberately does NOT do this: it is called immediately
 * after a successful claim, which is exactly when the window must stay open.)
 */
export function resetDeviceSession(): void {
  lastClaimOkAt = 0
  rediagnosing = false
  setState({ kind: 'idle' })
}

/** The transport calls this when a request comes back 401 / device_evicted /
 *  device_not_registered. */
export function reportSessionFailure(f: SessionFailure): void {
  // A "limit" prompt in progress is a live conversation with the user; a 401
  // burst from a background poll must not wipe it out from under their finger.
  if (state.kind === 'limit') return
  // Same rule one step further: a wall is already up and the user is reading
  // it. A later, vaguer failure (`signed_out`) must not overwrite a specific
  // one (`evicted`) and swap the copy under their eyes mid-sentence.
  if (state.kind === 'evicted' && f.kind !== 'device_unregistered') return

  // Fresh off a successful claim: do not believe a device 403 that was decided
  // before the claim landed. Ask the server again instead of walling the user.
  if (
    (f.kind === 'device_evicted' || f.kind === 'device_unregistered')
    && Date.now() - lastClaimOkAt < CLAIM_GRACE_MS
  ) {
    if (rediagnosing) return
    rediagnosing = true
    void claimDevice(undefined, { cause: 'interrupted' }).finally(() => { rediagnosing = false })
    return
  }

  if (f.kind === 'device_evicted') { setState({ kind: 'evicted', message: f.message }); return }
  if (f.kind === 'device_unregistered') {
    // The server now refuses an UNREGISTERED device on a full account on EVERY
    // route, not only at claim time, so this arrives from any screen. It is not
    // a message to render — it is a call to make: `claim` either comes back 409
    // with the three sessions (raising the interstitial, which is the actual
    // recovery) or 200 because a slot has since freed, in which case the whole
    // thing resolves without the user seeing anything. `claimDevice` sets
    // `skipSessionHandler`, so it cannot re-enter this function.
    //
    // This is now the FALLBACK path. `lib/firebase.ts` registers `claimDevice`
    // as the transport's device-recovery handler, so the ordinary case is
    // repaired one layer down — claim, then retry the failed request once — and
    // never reaches here. It stays because the recovery is registered at init:
    // anything that fetches before that (or any shell that never registers one)
    // still gets the interstitial rather than a dead end.
    void claimDevice(undefined, { cause: 'interrupted' })
    return
  }
  setState({ kind: 'signed_out' })
}

interface ClaimResponse {
  ok: true; deviceId: string; limit: number; exempt: boolean
  evicted: string | null; devices: DeviceSummary[]
}
interface LimitBody { limit?: number; devices?: DeviceSummary[] }

/**
 * Register this device against the signed-in account.
 *
 * Idempotent server-side for a device that is already active (a reload, a token
 * refresh, a second tab), so it is safe to call on every sign-in tick.
 *
 * `evict` is the device the USER picked in the interstitial. The server never
 * chooses (locked decision 3), which is why this is a second, explicit call
 * rather than a `force: true` on the first one.
 */
export async function claimDevice(
  evict?: string,
  opts: { cause?: LimitCause; attempt?: number } = {},
): Promise<'ok' | 'limit' | 'inert'> {
  const cause: LimitCause = opts.cause ?? 'signin'
  const attempt = opts.attempt ?? 0
  const d = deviceDescriptor()
  try {
    await api<ClaimResponse>('/me/devices/claim', {
      method: 'POST',
      body: { deviceId: d.deviceId, label: d.label, platform: d.platform, native: d.native, ...(evict ? { evict } : {}) },
      // This call is the diagnosis, so it must not re-enter the global handler
      // that a failure here would otherwise trigger — that is a loop.
      skipSessionHandler: true,
    })
    // Opens the anti-recurrence window BEFORE the gate clears, so a stale 403
    // that arrives in the same tick as the success cannot re-raise a wall.
    lastClaimOkAt = Date.now()
    if (state.kind === 'limit' || state.kind === 'evicted') clearDeviceGate()
    return 'ok'
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const body = (err.body || {}) as LimitBody
      setState({
        kind: 'limit',
        devices: Array.isArray(body.devices) ? body.devices : [],
        limit: typeof body.limit === 'number' ? body.limit : 3,
        busy: false,
        error: null,
        // A limit raised while a limit prompt is already up keeps the original
        // cause: the user is mid-decision and the sentence must not change.
        cause: state.kind === 'limit' ? state.cause : cause,
      })
      return 'limit'
    }
    if (err instanceof ApiError && err.status === 403 && (err.body as { code?: string } | null)?.code === 'device_evicted') {
      // ONE DELAYED RETRY, and it is not superstition. The server identifies the
      // evicted session by its `auth_time`, which has ONE-SECOND granularity —
      // so if a user's claim, eviction and re-sign-in all land inside the same
      // wall-clock second, the fresh session is indistinguishable from the dead
      // one and is refused (functions/src/devices/decide.ts::isEvictedToken
      // documents this as the last second-granularity residual, and prefers
      // this direction because the alternative lets an evicted session escape
      // permanently). Waiting past the second boundary and asking once more
      // turns that residual into something no user can perceive. A second
      // failure is a real eviction and gets the interstitial.
      if (attempt === 0) {
        await new Promise((r) => { setTimeout(r, 1200) })
        return claimDevice(evict, { cause, attempt: 1 })
      }
      setState({ kind: 'evicted', message: err.message })
      return 'inert'
    }
    // 404 (feature off — today's prod), a network blip, a 500: the cap is not
    // load-bearing enough to interrupt anyone over.
    return 'inert'
  }
}

/** Mark the interstitial busy / failed while an eviction is in flight. */
export function setLimitBusy(busy: boolean, error: string | null = null): void {
  if (state.kind !== 'limit') return
  setState({ ...state, busy, error })
}

/**
 * Hand this device's slot back on sign-out, so the next machine gets a free
 * slot instead of an interstitial. Best-effort and time-boxed: sign-out must
 * never hang on it, and a failure just means the slot is reclaimed the next
 * time the user hits their limit and evicts something.
 */
export async function releaseDevice(): Promise<void> {
  try {
    await Promise.race([
      api(`/me/devices/${encodeURIComponent(getDeviceId())}/revoke`, {
        method: 'POST', skipSessionHandler: true,
      }),
      new Promise((resolve) => { setTimeout(resolve, 2500) }),
    ])
  } catch { /* best effort, always */ }
}

/**
 * The manage-devices list (`GET /me/devices`).
 *
 * ── WHY THE 404 IS ITS OWN ANSWER, NOT AN ERROR ───────────────────────────
 * This route 404s whenever `DEVICE_LIMIT_LIVE` is unset, which is TODAY'S
 * PROD, and the old signature collapsed that into the same `null` as a 500.
 * A settings screen cannot tell the difference and would have had to choose
 * between showing "couldn't load your sessions" to every user on earth, or
 * hiding a genuine outage. `off` means the feature simply is not there: the
 * nav rows do not render at all, and a deep link explains itself instead of
 * erroring.
 */
export type DeviceListResult =
  | { kind: 'ok'; devices: DeviceSummary[]; limit: number; exempt: boolean; currentDeviceId: string | null }
  | { kind: 'off' }
  | { kind: 'error'; message: string }

interface DeviceListBody {
  devices?: DeviceSummary[]; limit?: number; exempt?: boolean; currentDeviceId?: string | null
}

function shapeList(body: DeviceListBody): DeviceListResult {
  return {
    kind: 'ok',
    devices: Array.isArray(body.devices) ? body.devices : [],
    limit: typeof body.limit === 'number' ? body.limit : 3,
    exempt: body.exempt === true,
    currentDeviceId: typeof body.currentDeviceId === 'string' ? body.currentDeviceId : getDeviceId(),
  }
}

export async function listDevices(): Promise<DeviceListResult> {
  try {
    return shapeList(await api<DeviceListBody>('/me/devices'))
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { kind: 'off' }
    return {
      kind: 'error',
      message: err instanceof Error && err.message ? err.message : 'Couldn’t load your sessions.',
    }
  }
}

/**
 * Sign ONE device out (`POST /me/devices/:id/revoke`).
 *
 * `gone` (the server's 404 `unknown_device`) is deliberately NOT an error: the
 * session had already ended, so the row the user pressed is stale and must
 * disappear anyway. A stale row that cannot be removed is its own small dead
 * end — the user presses it forever and nothing happens.
 */
export type RevokeResult =
  | { kind: 'ok'; devices: DeviceSummary[]; limit: number }
  | { kind: 'gone' }
  | { kind: 'off' }
  | { kind: 'error'; message: string }

export async function revokeDevice(deviceId: string): Promise<RevokeResult> {
  try {
    const r = await api<{ ok: true; devices?: DeviceSummary[]; limit?: number }>(
      `/me/devices/${encodeURIComponent(deviceId)}/revoke`,
      { method: 'POST', skipSessionHandler: true },
    )
    return {
      kind: 'ok',
      devices: Array.isArray(r.devices) ? r.devices : [],
      limit: typeof r.limit === 'number' ? r.limit : 3,
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // 404 is ambiguous here: the whole router 404s while the flag is off, and
      // a single unknown device 404s with a code. Only the coded one is "gone".
      const code = (err.body as { code?: string } | null)?.code
      return code === 'unknown_device' ? { kind: 'gone' } : { kind: 'off' }
    }
    return {
      kind: 'error',
      message: err instanceof Error && err.message ? err.message : 'Couldn’t sign that session out.',
    }
  }
}

/**
 * ── THE EXIT THAT MAKES A LOST DEVICE RECOVERABLE ─────────────────────────
 *
 * Sign out every session that is not this one, then claim. This is reachable
 * from a device that holds NO slot, because `functions/src/devices/index.ts`
 * sets `deviceSelfService = true` for the whole router — so a user on a
 * replacement phone, in private mode, or who cleared their storage can always
 * clear their own slate without contacting anyone.
 *
 * That is the answer to "I lost the laptop and I can't identify any of these
 * three rows", which the pick-one interstitial on its own cannot give.
 * Failures are surfaced, not swallowed: this button is somebody's last resort.
 */
export async function revokeOtherSessions(devices: DeviceSummary[]): Promise<'ok' | 'partial' | 'error'> {
  const mine = getDeviceId()
  const targets = devices.filter((d) => !d.current && d.deviceId !== mine)
  let failures = 0
  for (const d of targets) {
    const r = await revokeDevice(d.deviceId)
    // `gone` is a success: the slot we wanted freed is free.
    if (r.kind === 'error' || r.kind === 'off') failures += 1
  }
  const claimed = await claimDevice(undefined, { cause: 'interrupted' })
  if (claimed === 'ok') return 'ok'
  if (failures > 0 && failures === targets.length) return 'error'
  return 'partial'
}
